import {matchPath} from 'react-router-dom';
import type {BreadcrumbItem} from '@/components/breadcrumbs/Breadcrumbs';

export type BreadcrumbData = Record<string, string | null | undefined>;

export type BreadcrumbRoute = {
  path: string;
  getItems: (
    params: Record<string, string>,
    data: BreadcrumbData
  ) => BreadcrumbItem[];
};

/**
 * Creates a typed array of breadcrumb routes for a specific domain.
 *
 * The generic parameter `T` constrains the `data` argument within the domain,
 * providing autocomplete and type safety when authoring routes. The returned
 * array is widened to `BreadcrumbRoute[]` so it can be aggregated with other
 * domains.
 */
export function createBreadcrumbRoutes<T extends BreadcrumbData>(
  routes: {
    path: string;
    getItems: (params: Record<string, string>, data: T) => BreadcrumbItem[];
  }[]
): BreadcrumbRoute[] {
  return routes as BreadcrumbRoute[];
}

/**
 * Matches `pathname` against a list of breadcrumb routes, returning the first
 * match with extracted URL params, or `null` if none match.
 */
export function matchBreadcrumbRoutes(
  routes: BreadcrumbRoute[],
  pathname: string
): {route: BreadcrumbRoute; params: Record<string, string>} | null {
  return routes.reduce<{
    route: BreadcrumbRoute;
    params: Record<string, string>;
  } | null>((acc, route) => {
    if (acc) return acc;
    const match = matchPath(route.path, pathname);
    return match
      ? {route, params: match.params as Record<string, string>}
      : null;
  }, null);
}
